import { useNavigation } from '@react-navigation/native'
import { NativeStackNavigationProp } from '@react-navigation/native-stack'
import { MenuStackParamList } from '@navigation'
import { Share } from 'react-native'
import Clipboard from '@react-native-clipboard/clipboard'
import { useGetProfile, useScreen } from '@hooks'
import { useLanguage } from '@context'
import { showErrorToast, showSuccessToast } from '@utils'

interface UseQRCodeResult {
  navigation: NativeStackNavigationProp<MenuStackParamList>
  userName: string
  profileUrl: string
  onShareProfile: () => void
  onCopyLink: () => void
  onDownload: () => void
}

export const useQRCode = (): UseQRCodeResult => {
  const navigation = useNavigation<NativeStackNavigationProp<MenuStackParamList>>()
  const { data: profile } = useGetProfile()
  const { t } = useLanguage()
  const { isWeb } = useScreen()

  const generateUserName = (name: string): string => {
    if (!name) return '@user'
    return `@${name.toLowerCase().replace(/\s+/g, '_')}`
  }

  const userName = generateUserName(profile?.fullName || '')
  // Shareable deep link uses the profile's id (matches `profile/:profileId` route
  // and DetailsGhostProfile's share button). `accountId` is undefined on the
  // current user's own `/profile/me` payload, which produced `/profile/undefined`.
  const profileUrl = `https://keeptouch.co/profile/${profile?.id ?? ''}`

  const onShareProfile = async () => {
    if (isWeb) {
      try {
        if (navigator.share) {
          await navigator.share({
            title: t('qrCode.shareTitle'),
            text: t('qrCode.shareMessage').replace('{url}', profileUrl),
            url: profileUrl,
          })
        } else {
          navigator.clipboard.writeText(profileUrl)
          showSuccessToast(t('qrCode.success'), t('qrCode.linkCopied'))
        }
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
      }
    } else {
      try {
        await Share.share({
          message: t('qrCode.shareMessage').replace('{url}', profileUrl),
          url: profileUrl,
          title: t('qrCode.shareTitle'),
        })
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
      }
    }
  }

  const onCopyLink = async () => {
    if (isWeb) {
      try {
        if (navigator.clipboard) {
          await navigator.clipboard.writeText(profileUrl)
          showSuccessToast(t('qrCode.success'), t('qrCode.linkCopied'))
        } else {
          Clipboard.setString(profileUrl)
          showSuccessToast(t('qrCode.success'), t('qrCode.linkCopied'))
        }
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.copyError'))
      }
    } else {
      Clipboard.setString(profileUrl)
      showSuccessToast(t('qrCode.success'), t('qrCode.linkCopied'))
    }
  }

  const onDownload = async () => {
    if (isWeb) {
      // Web: find the QR code SVG and trigger download
      try {
        const svgElement = document.querySelector('svg[viewBox]') as SVGSVGElement | null
        if (!svgElement) {
          showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
          return
        }
        const serializer = new XMLSerializer()
        const svgString = serializer.serializeToString(svgElement)
        const blob = new Blob([svgString], { type: 'image/svg+xml' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `keeptouch-qrcode-${userName.replace('@', '')}.svg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
      }
    } else {
      // Native: use Share to save/share the QR code URL
      try {
        await Share.share({
          message: profileUrl,
          title: t('qrCode.shareTitle'),
        })
      } catch (error) {
        showErrorToast(t('qrCode.error'), t('qrCode.shareError'))
      }
    }
  }

  return {
    navigation,
    userName,
    profileUrl,
    onShareProfile,
    onCopyLink,
    onDownload,
  }
}
